import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { HttpClient, HttpBackend } from "@angular/common/http";

import {
	SAML,
	User,
	SamlAlias,
	SamlAliasUpdateRequest,
	Resource,
	ResourceUpdateRequest,
	ResourceCreateRequest,
	SamlAliasCreateRequest,
	UserCreateRequest,
	UserUpdateRequest
} from "@impulse/sdp-shared-models";

import { Session, SessionStatus, DisconnectSessions } from "../../sessions";
import {
	HealthCheckResponse,
	CurrentUserData,
	GatewayState,
	GatewayReturnType,
	AccountInfo
} from "../../models";

import { environment } from "environments/environment";
import { AuthenticationService } from "../authentication/authentication.service";
import { NewSamlIdpRequest } from "../../administrative/users/saml-wizard/models";
import { IdpAuth } from "../../administrative/users/saml-wizard/models";
import { GatewayUpdate } from "../../administrative/gateways";
import { GatewayView } from "../../administrative/gateways/gateway-view";
import { GatewayManifestDictionary } from "../../administrative/gateways/models";
import { UserName } from "../name-change";

const API_HOST = environment.API_HOST;

@Injectable()
export class ApiGatewayService {
	private readonly userVersion = "v5";
	private readonly resourceVersion = "v3";
	private readonly samlAliasVersion = "v1";
	private readonly samlProviderVersion = "v2";
	private readonly gatewayVersion = "v6";
	private readonly sessionsVersion = "v7";

	private http: HttpClient;

	constructor(
		private httpAuth: HttpClient,
		handler: HttpBackend,
		private authService: AuthenticationService
	) {
		/*
		 * When injected, HttpBackend dispatches requests directly to the backend,
		 * without going through the interceptor chain
		*/
		this.http = new HttpClient(handler);
	}

	public getAccountInfo() {
		return this.convertObservableToPromise<AccountInfo>(this.httpAuth.get(`${API_HOST}/main/login/info`));
	}

	public deleteAccount() {
		return this.convertObservableToPromise<string>(this.httpAuth.delete(`${API_HOST}/main/account`));
	}

	public createUser(data: UserCreateRequest) {
		return this.convertObservableToPromise<User>(this.httpAuth.post(`${API_HOST}/user/${this.userVersion}`, data));
	}

	public resendUserInvitation(email: string, accountName: string) {
		return this.convertObservableToPromise<void>(
			this.http.post(`${API_HOST}/user/${this.userVersion}/resend?accountName=${accountName}`, { email: email }));
	}

	// calls the admin update api
	public updateUser(data: UserUpdateRequest, emailIn: string) {
		const email = encodeURIComponent(emailIn);
		return this.convertObservableToPromise<User>(this.httpAuth.put(`${API_HOST}/user/${this.userVersion}/${email}`, data));
	}

	public getUser(email: string) {
		const encodedEmail = encodeURIComponent(email);
		return this.convertObservableToPromise<User>(this.httpAuth.get(`${API_HOST}/user/${this.userVersion}/${encodedEmail}`));
	}

	public getAllUsers() {
		return this.convertObservableToPromise<User[]>(this.httpAuth.get(`${API_HOST}/user/${this.userVersion}`));
	}

	public deleteUser(email: string) {
		const encodedEmail = encodeURIComponent(email);
		return this.convertObservableToPromise<User[]>(this.httpAuth.delete(`${API_HOST}/user/${this.userVersion}/${encodedEmail}`));
	}

	public updateHideWizard(value: boolean) {
		return this.convertObservableToPromise<CurrentUserData>(this.httpAuth.put(
			`${API_HOST}/user/self/${this.userVersion}`,
			{ hideWizard: value }
		));
	}

	public updateMfaDevice(mfaDevice: User.MfaDevice) {
		return this.convertObservableToPromise<CurrentUserData>(this.httpAuth.put(
			`${API_HOST}/user/self/${this.userVersion}`,
			{ mfaDevice: mfaDevice }
		));
	}

	public updateUserName(name: UserName) {
		return this.convertObservableToPromise<CurrentUserData>(this.httpAuth.put(
			`${API_HOST}/user/self/${this.userVersion}`,
			name
		));
	}

	public getSelf() {
		return this.convertObservableToPromise<CurrentUserData>(this.httpAuth.get(`${API_HOST}/user/self/${this.userVersion}`));
	}

	public createApplication(data: ResourceCreateRequest) {
		sanitizeApplication(data);
		return this.convertObservableToPromise<Resource[]>(this.httpAuth.post(`${API_HOST}/resource/${this.resourceVersion}`, data));
	}

	public updateApplication(data: ResourceUpdateRequest, resourceUrlName: string) {
		sanitizeApplication(data);
		return this.convertObservableToPromise<Resource[]>(
			this.httpAuth.put(`${API_HOST}/resource/${this.resourceVersion}/${resourceUrlName}`, data));
	}

	public async getApplication(urlName: string) {
		return await this.convertObservableToPromise<Resource>(this.httpAuth.get(`${API_HOST}/resource/${this.resourceVersion}/${urlName}`));
	}

	public async getAllApplications() {
		return await this.convertObservableToPromise<Resource[]>(this.httpAuth.get(`${API_HOST}/resource/${this.resourceVersion}`));
	}

	public deleteApplication(urlName: string) {
		return this.convertObservableToPromise<Resource[]>(this.httpAuth.delete(`${API_HOST}/resource/${this.resourceVersion}/${urlName}`));
	}

	public healthCheck() {
		return this.convertObservableToPromise<HealthCheckResponse>(this.httpAuth.get(`${API_HOST}/user/healthCheck`));
	}

	public getIdentityProviderOptions() {
		return this.convertObservableToPromise<string[]>(this.httpAuth.get(`${API_HOST}/saml/idp/${this.samlProviderVersion}/options`));
	}

	public getIdentityProviderOption(entityId: string) {
		return this.convertObservableToPromise<SAML.ServiceProvider.IDPOptions.Field[]>
			(this.httpAuth.get(`${API_HOST}/saml/sp/options?idpName=${encodeURIComponent(entityId)}`));
	}

	public createSamlIdentityProvider(data: NewSamlIdpRequest) {
		return this.convertObservableToPromise<SAML.IdentityProvider[]>(
			this.httpAuth.post(`${API_HOST}/saml/idp/${this.samlProviderVersion}`, data)
		);
	}

	public updateSamlIdentityProvider(data: NewSamlIdpRequest, id: string) {
		return this.convertObservableToPromise<SAML.IdentityProvider[]>(
			this.httpAuth.put(`${API_HOST}/saml/idp/${this.samlProviderVersion}/${encodeURIComponent(id)}`, data));
	}

	public getIdentityProviders() {
		return this.convertObservableToPromise<SAML.IdentityProvider[]>(this.httpAuth.get(`${API_HOST}/saml/idp/${this.samlProviderVersion}`));
	}

	public deleteIdentityProvider(id: string) {
		return this.convertObservableToPromise<SAML.IdentityProvider[]>(
			this.httpAuth.delete(`${API_HOST}/saml/idp/${this.samlProviderVersion}/${encodeURIComponent(id)}`));
	}

	public getUrl(url: string) {
		return this.httpAuth.get(url, { responseType: "text" });
	}

	public getIdentityProvidersForAuth(accountName: string) {
		return this.convertObservableToPromise<IdpAuth[]>(this.http.get(`${API_HOST}/samlauth/idps?accountName=${accountName}`));
	}

	public sendIdentityProviderForAuth(accountName: string, entityId: string) {
		this.http.get(`${API_HOST}/samlauth?accountName=${accountName}&entityId=${entityId}`);
	}

	public getSessions(status: SessionStatus) {
		return this.convertObservableToPromise<Session[]>(this.httpAuth.get(`${API_HOST}/sessions/${this.sessionsVersion}/sessions/${status}`));
	}

	public getGateway(uuid: string) {
		return this.convertObservableToPromise<GatewayView>(this.httpAuth.get(`${API_HOST}/gateway/${this.gatewayVersion}/${uuid}`));
	}

	public getGateways(value: GatewayReturnType) {
		return this.convertObservableToPromise<GatewayState>(this.httpAuth.get(
			`${API_HOST}/gateway/${this.gatewayVersion}?returnValues=${value}`
		));
	}

	public activateGateway(uuid: string) {
		return this.convertObservableToPromise<GatewayState>
			(this.httpAuth.put(`${API_HOST}/gateway/${this.gatewayVersion}/activate/${uuid}?returnValues=ALL`, undefined));
	}

	public deactivateGateway(uuid: string) {
		return this.convertObservableToPromise<GatewayState>
			(this.httpAuth.put(`${API_HOST}/gateway/${this.gatewayVersion}/deactivate/${uuid}?returnValues=ALL`, undefined));
	}

	public deleteGateway(uuid: string) {
		return this.convertObservableToPromise<GatewayState>(this.httpAuth.delete(
			`${API_HOST}/gateway/${this.gatewayVersion}/${uuid}?returnValues=ALL`
		));
	}

	public acceptGateway(uuid: string) {
		return this.convertObservableToPromise<GatewayState>
			(this.httpAuth.put(`${API_HOST}/gateway/${this.gatewayVersion}/accept/${uuid}?returnValues=ALL`, undefined));
	}

	public updateGateway(uuid: string, value: GatewayUpdate) {
		return this.convertObservableToPromise<GatewayState>(this.httpAuth.put(
			`${API_HOST}/gateway/${this.gatewayVersion}/${uuid}?returnValues=ALL`,
			value
		));
	}

	public getDownloadManifest(manifestUrl: string) {
		return this.convertObservableToPromise<GatewayManifestDictionary>(this.http.get(manifestUrl));
	}

	public createSamlAlias(data: SamlAliasCreateRequest) {
		return this.convertObservableToPromise<void>(this.httpAuth.post(`${API_HOST}/samlalias/${this.samlAliasVersion}/`, data));
	}

	public updateSamlAlias(id: string, data: SamlAliasUpdateRequest) {
		return this.convertObservableToPromise<void>(this.httpAuth.put(`${API_HOST}/samlalias/${this.samlAliasVersion}/${id}`, data));
	}

	public getSamlAlias(id: string) {
		return this.convertObservableToPromise<SamlAlias>(this.httpAuth.get(`${API_HOST}/samlalias/${this.samlAliasVersion}/${id}`));
	}

	public getAllSamlAliases() {
		return this.convertObservableToPromise<SamlAlias[]>(this.httpAuth.get(`${API_HOST}/samlalias/${this.samlAliasVersion}`));
	}

	public deleteSamlAlias(id: string) {
		return this.convertObservableToPromise<void>(this.httpAuth.delete(`${API_HOST}/samlalias/${this.samlAliasVersion}/${id}`));
	}

	public disconnectSessions(request: DisconnectSessions.Request) {
		return this.convertObservableToPromise<DisconnectSessions.Response>(this.httpAuth.post(
			`${API_HOST}/sessions/${this.sessionsVersion}/disconnect`,
			request
		));
	}

	private convertObservableToPromise<T>(obs: Observable<Object>) {
		return new Promise<T>((resolve, reject) => {
			obs.subscribe(
				// All api calls come through here, 'any' catches them all
				(res: any) => {
					if (!!res) {
						const result = <T>res;
						resolve(result);
					} else {
						reject();
						if (res.status === 401) {
							this.authService.logout();
						}
					}
				},
				(err) => {
					reject(err);
				}
			);
		});
	}
}

/**
 * Back-end validation requires that some fields be ommitted (undefined) instead of the empty string.
 */
function sanitizeApplication(application: ResourceCreateRequest | ResourceUpdateRequest) {
	if (!application.urlSuffix) {
		delete application.urlSuffix;
	}
}
